// PostgREST-compatible client shim. Lets the 25 admin views + public dashboard
// keep their `supa.client.from(table).select().eq()...` call sites while talking
// to the Cloudflare Worker (/api/query + /api/mutate) instead of Supabase.
// Supports the subset of the supabase-js query builder the app actually uses.

type Filter = { col: string; op: string; val: unknown };
type Order = { col: string; ascending: boolean };
type Single = 'single' | 'maybe' | null;
export interface PgResult<T = unknown> {
  data: T;
  error: { message: string; code?: string } | null;
  count?: number;
}

async function call(path: string, payload: unknown): Promise<PgResult> {
  try {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok && res.status !== 200) {
      const t = await res.text().catch(() => '');
      return { data: null, error: { message: `HTTP ${res.status} ${t}`.trim(), code: String(res.status) } };
    }
    return (await res.json()) as PgResult;
  } catch (e) {
    return { data: null, error: { message: (e as Error).message } };
  }
}

class QueryBuilder<T = unknown> implements PromiseLike<PgResult<T>> {
  private filters: Filter[] = [];
  private _order: Order[] = [];
  private _limit?: number;
  private _offset?: number;
  private _single: Single = null;
  private _count = false;
  private _head = false;
  private op: 'select' | 'insert' | 'upsert' | 'update' | 'delete' = 'select';
  private values: unknown = null;

  constructor(private table: string) {}

  // ---- read shaping ----
  select(_cols?: string, opts?: { count?: string; head?: boolean }): this {
    if (this.op === 'select') {
      /* projection ignored; worker returns full rows */
    }
    if (opts?.count) this._count = true;
    if (opts?.head) this._head = true;
    return this;
  }
  order(col: string, opts?: { ascending?: boolean }): this {
    this._order.push({ col, ascending: opts?.ascending !== false });
    return this;
  }
  limit(n: number): this { this._limit = n; return this; }
  range(from: number, to: number): this { this._offset = from; this._limit = to - from + 1; return this; }
  single(): this { this._single = 'single'; return this; }
  maybeSingle(): this { this._single = 'maybe'; return this; }

  // ---- write ops ----
  insert(values: unknown): this { this.op = 'insert'; this.values = values; return this; }
  upsert(values: unknown): this { this.op = 'upsert'; this.values = values; return this; }
  update(values: unknown): this { this.op = 'update'; this.values = values; return this; }
  delete(): this { this.op = 'delete'; return this; }

  // ---- filters ----
  eq(col: string, val: unknown): this { this.filters.push({ col, op: 'eq', val }); return this; }
  neq(col: string, val: unknown): this { this.filters.push({ col, op: 'neq', val }); return this; }
  gt(col: string, val: unknown): this { this.filters.push({ col, op: 'gt', val }); return this; }
  gte(col: string, val: unknown): this { this.filters.push({ col, op: 'gte', val }); return this; }
  lt(col: string, val: unknown): this { this.filters.push({ col, op: 'lt', val }); return this; }
  lte(col: string, val: unknown): this { this.filters.push({ col, op: 'lte', val }); return this; }
  like(col: string, val: unknown): this { this.filters.push({ col, op: 'like', val }); return this; }
  ilike(col: string, val: unknown): this { this.filters.push({ col, op: 'ilike', val }); return this; }
  in(col: string, val: unknown[]): this { this.filters.push({ col, op: 'in', val }); return this; }
  is(col: string, val: unknown): this { this.filters.push({ col, op: 'is', val }); return this; }
  not(col: string, op: string, val: unknown): this { this.filters.push({ col, op: 'not', val: { op, val } }); return this; }
  or(str: string): this { this.filters.push({ col: '_', op: 'or', val: str }); return this; }
  contains(col: string, val: unknown): this { this.filters.push({ col, op: 'like', val: `%${JSON.stringify(val).slice(1, -1)}%` }); return this; }

  private exec(): Promise<PgResult<T>> {
    if (this.op === 'select') {
      return call('/api/query', {
        table: this.table, filters: this.filters, order: this._order,
        limit: this._limit, offset: this._offset, single: this._single,
        count: this._count || this._head,
      }) as Promise<PgResult<T>>;
    }
    return (async () => {
      const r = (await call('/api/mutate', {
        table: this.table, op: this.op, values: this.values, filters: this.filters,
      })) as PgResult<T>;
      if (this._single && Array.isArray(r.data)) {
        return { ...r, data: ((r.data as unknown[])[0] ?? null) as T };
      }
      return r;
    })();
  }

  then<R1 = PgResult<T>, R2 = never>(
    onfulfilled?: ((v: PgResult<T>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.exec().then(onfulfilled, onrejected);
  }
}

export function createPgFrom() {
  return (table: string) => new QueryBuilder(table);
}
